'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import Image from 'next/image';
import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import type { GalleryImage } from '@/lib/gallery-image';
import { cn } from '@/lib/utils';

/** Mouse drag on overflow-x strip; touch keeps native momentum scroll (same idea as timetable + desktop drag). */
function useMouseDragHorizontalScroll(ref: RefObject<HTMLDivElement | null>) {
    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        function attach(node: HTMLDivElement) {
            let dragging = false;
            let startX = 0;
            let startScroll = 0;
            let activeId: number | null = null;

            function onPointerDown(e: PointerEvent) {
                if (e.pointerType !== 'mouse' || e.button !== 0) return;
                dragging = true;
                activeId = e.pointerId;
                startX = e.clientX;
                startScroll = node.scrollLeft;
                node.setPointerCapture(e.pointerId);
                node.classList.add('cursor-grabbing');
            }

            function onPointerMove(e: PointerEvent) {
                if (!dragging || activeId !== e.pointerId) return;
                node.scrollLeft = startScroll - (e.clientX - startX);
            }

            function onPointerUp(e: PointerEvent) {
                if (e.pointerId !== activeId) return;
                dragging = false;
                const id = activeId;
                activeId = null;
                try {
                    if (id !== null) node.releasePointerCapture(id);
                } catch {
                    /* released */
                }
                node.classList.remove('cursor-grabbing');
            }

            node.addEventListener('pointerdown', onPointerDown);
            node.addEventListener('pointermove', onPointerMove);
            node.addEventListener('pointerup', onPointerUp);
            node.addEventListener('pointercancel', onPointerUp);

            return () => {
                node.removeEventListener('pointerdown', onPointerDown);
                node.removeEventListener('pointermove', onPointerMove);
                node.removeEventListener('pointerup', onPointerUp);
                node.removeEventListener('pointercancel', onPointerUp);
                node.classList.remove('cursor-grabbing');
            };
        }

        return attach(el);
    }, [ref]);
}

function GalleryImageTile({
    img,
    priority,
    className,
}: {
    img: GalleryImage;
    priority?: boolean;
    className?: string;
}) {
    return (
        <div
            className={cn(
                'relative aspect-[4/3] overflow-hidden rounded-xl bg-night-light ring-1 ring-zinc-800',
                className
            )}
        >
            <Image
                src={img.url}
                alt={img.alt}
                width={img.width}
                height={img.height}
                className="h-full w-full object-cover"
                draggable={false}
                sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 88vw"
                priority={priority}
                unoptimized={img.url.startsWith('http://')}
            />
        </div>
    );
}

function GalleryImageCaption({ img }: { img: GalleryImage }) {
    if (!img.title) return null;
    return <p className="mt-3 text-center font-medium text-sm text-zinc-400">{img.title}</p>;
}

function EditionGalleryLightbox({
    images,
    index,
    open,
    onOpenChange,
    onPrev,
    onNext,
}: {
    images: GalleryImage[];
    index: number;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onPrev: () => void;
    onNext: () => void;
}) {
    const safeIndex = Math.min(Math.max(0, index), Math.max(0, images.length - 1));
    const current = images[safeIndex];
    const count = images.length;
    const showNav = count > 1;

    useEffect(() => {
        if (!open) return;
        function onKey(e: KeyboardEvent) {
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                onPrev();
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                onNext();
            }
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onPrev, onNext]);

    if (!current) return null;

    const navButtonClass =
        'pointer-events-auto absolute top-1/2 z-10 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-zinc-600 bg-night/90 text-white shadow-lg backdrop-blur-sm transition-colors hover:border-zinc-500 hover:bg-night md:h-14 md:w-14';

    /** Viewport box for the photo; sized by viewport, not CMS dimensions (avoids tiny layout for small transforms). */
    const viewBoxMaxH = 'min(92dvh, calc(100vh - 7rem))';
    const viewBoxMaxW = 'min(calc(100vw - 2rem), 1600px)';

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-[70] animate-fade-in bg-black/97 backdrop-blur-md" />
                <Dialog.Content
                    className="fixed inset-0 z-[71] flex flex-col bg-transparent outline-none"
                    aria-describedby={undefined}
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    onPointerDown={(e) => {
                        if ((e.target as HTMLElement).closest('[data-lightbox-chrome]')) return;
                        onOpenChange(false);
                    }}
                >
                    <Dialog.Title className="sr-only">
                        Gallery — image {safeIndex + 1} of {count}
                    </Dialog.Title>

                    <div
                        data-lightbox-chrome
                        className="flex h-14 shrink-0 items-center justify-between border-zinc-800/80 border-b bg-night/90 px-4 backdrop-blur-md md:h-16 md:px-6"
                    >
                        {showNav ? (
                            <p className="font-medium text-sm text-zinc-500">
                                {safeIndex + 1} / {count}
                            </p>
                        ) : (
                            <span />
                        )}
                        <Dialog.Close asChild>
                            <button
                                type="button"
                                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-white"
                                aria-label="Close gallery"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </Dialog.Close>
                    </div>

                    <div className="relative min-h-0 flex-1 md:px-4 md:pb-4">
                        <div className="relative mx-auto flex h-full max-h-[calc(100vh-3.5rem)] max-w-[min(96rem,calc(100vw-2rem))] items-center justify-center md:max-h-[calc(100vh-4rem)]">
                            {showNav ? (
                                <button
                                    type="button"
                                    data-lightbox-chrome
                                    className={cn(navButtonClass, 'left-1 md:left-2')}
                                    onClick={onPrev}
                                    aria-label="Previous image"
                                >
                                    <ChevronLeft className="h-7 w-7 md:h-8 md:w-8" />
                                </button>
                            ) : null}

                            <div
                                className="flex w-full max-w-full flex-1 items-center justify-center px-12 md:px-20"
                                onPointerDown={(e) => e.stopPropagation()}
                            >
                                <div
                                    className="relative mx-auto w-full shrink-0"
                                    style={{
                                        height: viewBoxMaxH,
                                        maxHeight: viewBoxMaxH,
                                        width: '100%',
                                        maxWidth: viewBoxMaxW,
                                    }}
                                >
                                    <Image
                                        src={current.url}
                                        alt={current.alt}
                                        fill
                                        className="object-contain object-center"
                                        sizes="(max-width: 768px) 100vw, 1600px"
                                        priority
                                        unoptimized={current.url.startsWith('http://')}
                                    />
                                    {current.title ? (
                                        <div className="pointer-events-none absolute right-0 bottom-0 left-0 bg-gradient-to-t from-black/85 via-black/55 to-transparent px-4 pt-12 pb-5 text-center md:px-8 md:pb-7">
                                            <p className="font-medium text-base text-white md:text-lg">
                                                {current.title}
                                            </p>
                                        </div>
                                    ) : null}
                                </div>
                            </div>

                            {showNav ? (
                                <button
                                    type="button"
                                    data-lightbox-chrome
                                    className={cn(navButtonClass, 'right-1 md:right-2')}
                                    onClick={onNext}
                                    aria-label="Next image"
                                >
                                    <ChevronRight className="h-7 w-7 md:h-8 md:w-8" />
                                </button>
                            ) : null}
                        </div>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}

export function EditionGallery({ images }: { images: GalleryImage[] }) {
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
    const galleryStripRef = useRef<HTMLDivElement>(null);
    useMouseDragHorizontalScroll(galleryStripRef);

    const goPrev = useCallback(() => {
        setLightboxIndex((i) => {
            if (i === null || images.length <= 1) return i;
            return (i - 1 + images.length) % images.length;
        });
    }, [images.length]);

    const goNext = useCallback(() => {
        setLightboxIndex((i) => {
            if (i === null || images.length <= 1) return i;
            return (i + 1) % images.length;
        });
    }, [images.length]);

    if (images.length === 0) {
        return null;
    }

    if (images.length === 1) {
        return (
            <>
                <div className="mx-auto max-w-3xl">
                    <button
                        type="button"
                        className="group block w-full cursor-zoom-in rounded-xl p-0 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
                        onClick={() => setLightboxIndex(0)}
                        aria-label={`Enlarge photo: ${images[0].alt}`}
                    >
                        <GalleryImageTile
                            img={images[0]}
                            priority
                            className="transition-transform group-hover:ring-2 group-hover:ring-gold/40"
                        />
                    </button>
                    <GalleryImageCaption img={images[0]} />
                </div>
                <EditionGalleryLightbox
                    images={images}
                    index={lightboxIndex ?? 0}
                    open={lightboxIndex !== null}
                    onOpenChange={(next) => {
                        if (!next) setLightboxIndex(null);
                    }}
                    onPrev={goPrev}
                    onNext={goNext}
                />
            </>
        );
    }

    return (
        <>
            {/* Touch: native scroll. Mouse: drag updates scrollLeft (narrow devtools / trackpad users). */}
            <div className="md:hidden">
                <div
                    ref={galleryStripRef}
                    className="scrollbar-hidden -mx-1 flex cursor-grab select-none snap-x snap-mandatory gap-3 overflow-x-auto overscroll-x-contain px-1 pb-1"
                >
                    {images.map((img, index) => (
                        <div
                            key={img.id}
                            className="w-[min(88vw,28rem)] shrink-0 snap-start sm:w-[min(85vw,32rem)]"
                        >
                            <GalleryImageTile img={img} priority={index === 0} />
                            <GalleryImageCaption img={img} />
                        </div>
                    ))}
                </div>
            </div>

            <div className="hidden gap-3 md:grid md:grid-cols-2 lg:grid-cols-3">
                {images.map((img, i) => (
                    <div key={img.id}>
                        <button
                            type="button"
                            className="group block w-full cursor-zoom-in rounded-xl p-0 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
                            onClick={() => setLightboxIndex(i)}
                            aria-label={`Enlarge photo: ${img.alt}`}
                        >
                            <GalleryImageTile
                                img={img}
                                className="transition-transform group-hover:ring-2 group-hover:ring-gold/40"
                            />
                        </button>
                        <GalleryImageCaption img={img} />
                    </div>
                ))}
            </div>

            <EditionGalleryLightbox
                images={images}
                index={lightboxIndex ?? 0}
                open={lightboxIndex !== null}
                onOpenChange={(next) => {
                    if (!next) setLightboxIndex(null);
                }}
                onPrev={goPrev}
                onNext={goNext}
            />
        </>
    );
}
